import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { trpc } from "../lib/trpc";
import { useAuth } from "../hooks/useAuth";

/**
 * Magic link callback page
 * Handles the magic link token and authenticates the user
 */
export default function MagicLinkCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("Authentication failed");
  const isAuthenticating = useRef(false);

  const handleSuccess = useCallback((data: any) => {
    if (!isAuthenticating.current) {
      return; // Guard against duplicate calls
    }

    isAuthenticating.current = false;
    // Tokens are set by the server via httpOnly cookies - only store user locally
    login(data.user);
    setStatus("success");

    // Navigate immediately to prevent component from staying mounted
    navigate("/", { replace: true });
  }, [login, navigate]);

  const handleError = useCallback((error: any) => {
    if (isAuthenticating.current) {
      setStatus("error");
      isAuthenticating.current = false;
      const message = error.message || "Invalid or expired magic link";

      // Set specific error titles based on the error message
      if (message.includes("expired")) {
        setErrorTitle("Link has expired");
        setErrorMessage("This magic link has expired. Please request a new one.");
      } else if (message.includes("Invalid")) {
        setErrorTitle("Invalid link");
        setErrorMessage("This magic link is not valid. Please request a new one.");
      } else {
        setErrorTitle("Authentication failed");
        setErrorMessage(message);
      }
    }
  }, []);

  const consumeMagicLink = trpc.auth.consumeMagicLink.useMutation({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useEffect(() => {
    // Prevent any execution if already authenticating
    if (isAuthenticating.current) {
      return;
    }

    // Get token from URL
    const token = searchParams.get("token");

    // Validate token is present
    if (!token) {
      setStatus("error");
      setErrorTitle("Missing token");
      setErrorMessage("No authentication token was provided. Please check your link and try again.");
      return;
    }

    // Validate token format (basic check - should be a reasonable length string)
    if (token.length < 10) {
      setStatus("error");
      setErrorTitle("Invalid token");
      setErrorMessage("The authentication token appears to be malformed. Please request a new magic link.");
      return;
    }

    // Mark as authenticating before making the call
    isAuthenticating.current = true;

    // Consume the magic link token
    consumeMagicLink.mutate({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="w-[400px]">
          <CardContent className="pt-6">
            {status === "loading" && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Verifying your link...</h3>
                <p className="text-sm text-muted-foreground">
                  Please wait while we log you in
                </p>
              </div>
            )}

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <CheckCircle2 className="w-16 h-16 text-green-700 dark:text-green-300 mx-auto mb-4" />
                </motion.div>
                <h3 className="font-semibold mb-2">Success!</h3>
                <p className="text-sm text-muted-foreground">
                  You're now logged in. Redirecting...
                </p>
              </motion.div>
            )}

            {status === "error" && (
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{errorTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6">{errorMessage}</p>
                <Button onClick={() => navigate("/login")}>
                  Back to login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
